/**
 * Valid directions for the fade effect
 */
export type FadeDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Maps direction to the appropriate gradient angle
 */
const directionToAngle: Record<FadeDirection, number> = {
  right: 90,
  left: 270,
  down: 180,
  up: 0
};

/**
 * A container that fades its background color to transparent
 * Usage: <fade-container direction="left" bg-color="#ff0000" midpoint="50"></fade-container>
 */
export class FadeContainer extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['direction', 'bg-color', 'midpoint'];
  }

  // Default values
  private direction: FadeDirection = 'right';
  private bgColor = '#000000';
  private midpoint = 50;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  /**
   * Update the component's style based on current attributes
   */
  private updateStyle(): void {
    if (!this.shadowRoot) return;

    const style = document.createElement('style');
    const angle = directionToAngle[this.direction];
    
    style.textContent = `
      :host {
        display: flex;
        background: linear-gradient(
          ${angle}deg,
          ${this.bgColor} 0%,
          ${this.bgColor} ${this.midpoint}%,
          transparent 100%
        );
      }
    `;

    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }
    this.shadowRoot.appendChild(style);
  }

  /**
   * Handle attribute changes
   */
  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'direction':
        if (newValue && ['left', 'right', 'up', 'down'].includes(newValue)) {
          this.direction = newValue as FadeDirection;
        }
        break;
      case 'bg-color':
        this.bgColor = newValue ?? '#000000';
        break;
      case 'midpoint':
        this.midpoint = Math.min(100, Math.max(0, parseInt(newValue ?? '50', 10)));
        break;
    }
    this.updateStyle();
  }
}

// Register the custom element
customElements.define('fade-container', FadeContainer); 